import { Location } from '@angular/common';
import { Component, OnInit, ViewEncapsulation, ViewChildren, QueryList } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GridsterConfig, GridsterItem, DisplayGrid, GridType, CompactType } from 'angular-gridster2';
import { MessageService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';

import { Account } from '@/model/entities/entity-model';
import { DbQueryService } from '@/shared/db-query.service';
import { DomainBaseComponent } from '@/shared/domain-base.component';
import { DomainService } from '@/shared/domain.service';
import {
  DashboardWidgetConfig,
  DashboardConfig,
  createDefaultDashboardConfig,
  WidgetType,
  WIDGET_TYPE_REGISTRY,
  WidgetTypeInfo,
} from './widgets/dashboard-widget.interface';
import { DashboardConfigService } from './services/dashboard-config.service';
import { WidgetPickerDialogComponent } from './widgets/widget-picker-dialog.component';
import { WidgetDataDialogComponent, WidgetDataDialogParams } from './widgets/widget-data-dialog.component';
import { JoInProcessWidgetComponent } from './widgets/jo-in-process-widget.component';
import { ChartJoStatsComponent } from '../chart-jo-stats.component';
import { ChartInvoiceStatsComponent } from '../chart-invoice-stats.component';
import { ChartInvoiceDelayComponent } from '../chart-invoice-delay.component';
import { ChartManufInventoryComponent } from '../chart-manuf-inventory.component';
import { ChartProductTypeInventoryComponent } from './widgets/chart-product-type-inventory.component';
import { ChartAccountFulfillmentTimeComponent } from './widgets/chart-account-fulfillment-time.component';
import { ChartManufacturerReceiptTimeComponent } from './widgets/chart-manufacturer-receipt-time.component';
import { AccountOverdueOrdersComponent } from './widgets/account-overdue-orders.component';
import { ChartWeeklyShipmentRevenueComponent } from './widgets/chart-weekly-shipment-revenue.component';
import { RedZoneComponent } from './widgets/red-zone.component';
import { DEFAULT_KPI_RANGE, KPI_RANGE_OPTIONS, KpiDates, KpiRangeFns, KpiRangePreset } from './kpi-range-fns';
import { DialogBaseComponent } from '@/shared/dialog-base.component';
import { ExcludedAccountsDialogComponent } from './excluded-accounts-dialog.component';

/** Interface for widgets that support data view/export */
interface ViewableWidget {
  exportToExcel(): Promise<void>;
  getGridData(): unknown[];
  getColumnDefs(): unknown[];
  getExportFileName(): string;
  getDialogTitle(): string;
}

@Component({
  selector: 'app-dashboard-container',
  templateUrl: './dashboard-container.component.html',
  styleUrls: ['./dashboard-container.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class DashboardContainerComponent extends DomainBaseComponent implements OnInit {
  @ViewChildren(JoInProcessWidgetComponent) joInProcessWidgets!: QueryList<JoInProcessWidgetComponent>;
  @ViewChildren(ChartJoStatsComponent) joStatsWidgets!: QueryList<ChartJoStatsComponent>;
  @ViewChildren(ChartInvoiceStatsComponent) invoiceStatsWidgets!: QueryList<ChartInvoiceStatsComponent>;
  @ViewChildren(ChartInvoiceDelayComponent) invoiceDelayWidgets!: QueryList<ChartInvoiceDelayComponent>;
  @ViewChildren(ChartManufInventoryComponent) manufInventoryWidgets!: QueryList<ChartManufInventoryComponent>;
  @ViewChildren(ChartProductTypeInventoryComponent) productTypeInventoryWidgets!: QueryList<ChartProductTypeInventoryComponent>;
  @ViewChildren(ChartAccountFulfillmentTimeComponent) accountFulfillmentTimeWidgets!: QueryList<ChartAccountFulfillmentTimeComponent>;
  @ViewChildren(ChartManufacturerReceiptTimeComponent) manufacturerReceiptTimeWidgets!: QueryList<ChartManufacturerReceiptTimeComponent>;
  @ViewChildren(AccountOverdueOrdersComponent) accountOverdueOrdersWidgets!: QueryList<AccountOverdueOrdersComponent>;
  @ViewChildren(ChartWeeklyShipmentRevenueComponent) weeklyShipmentRevenueWidgets!: QueryList<ChartWeeklyShipmentRevenueComponent>;
  @ViewChildren(RedZoneComponent) redZoneWidgets!: QueryList<RedZoneComponent>;

  title = 'Dashboard';
  dashboardConfig: DashboardConfig = createDefaultDashboardConfig();
  account: Account | null = null;
  allAccounts: Account[] = [];

  kpiRangeOptions = KPI_RANGE_OPTIONS;
  kpiPreset: KpiRangePreset = DEFAULT_KPI_RANGE.preset;
  kpiFromDate: Date | null = null;
  kpiToDate: Date | null = null;
  /** Always a new object on change, so the KPI widgets' ngOnChanges fires. Null = incomplete Date Range. */
  kpiDates: KpiDates | null = null;

  /** Excluded accounts put back in through Show Excluded Accounts. This view only: never saved,
   *  so the dashboard excludes them again the next time it opens. A new array on every change. */
  includedAccountIds: number[] = [];

  // Map to track widget components by their widget id
  private widgetComponentMap = new Map<string, unknown>();

  gridsterOptions: GridsterConfig = {
    gridType: GridType.Fit,
    displayGrid: DisplayGrid.OnDragAndResize,
    compactType: CompactType.None,
    pushItems: true,
    draggable: {
      enabled: true,
      ignoreContent: true,
      dragHandleClass: 'widget-header',
    },
    resizable: {
      enabled: true,
    },
    minCols: 12,
    maxCols: 12,
    minRows: 8,
    margin: 12,
    outerMargin: true,
    outerMarginTop: 12,
    outerMarginRight: 12,
    outerMarginBottom: 12,
    outerMarginLeft: 12,
    defaultItemCols: 3,
    defaultItemRows: 2,
    itemChangeCallback: this.onItemChange.bind(this),
    itemResizeCallback: this.onItemResize.bind(this),
  };

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,
    protected location: Location,
    protected dbQueryService: DbQueryService,
    private dashboardConfigService: DashboardConfigService,
    private messageService: MessageService,
    private primengDialogService: DialogService,
  ) {
    super(domainService);
  }

  async ngOnInit() {
    // Load accounts for account-specific widgets
    const allAccounts = await this.dbQueryService.getAccountsWithInvoices();
    this.allAccounts = allAccounts.sort((a, b) => a.accountName.localeCompare(b.accountName));

    // Load user's saved dashboard config
    this.dashboardConfig = await this.dashboardConfigService.loadConfig();
    const saved = this.dashboardConfig.kpiRange ?? DEFAULT_KPI_RANGE;
    this.kpiPreset = saved.preset;
    this.kpiFromDate = KpiRangeFns.parseIsoDay(saved.fromDate);
    this.kpiToDate = KpiRangeFns.parseIsoDay(saved.toDate);
    this.onKpiRangeChanged();

    this.isPageReady = true;
  }

  onKpiPresetChanged() {
    // Start a new Date Range from the range on screen, so the tiles are never blank on the switch.
    if (this.kpiPreset === 'dateRange' && this.kpiDates && !this.kpiFromDate && !this.kpiToDate) {
      this.kpiFromDate = this.kpiDates.from;
      this.kpiToDate = this.kpiDates.to;
    }
    this.onKpiRangeChanged();
  }

  onKpiRangeChanged() {
    const isDateRange = this.kpiPreset === 'dateRange';
    this.dashboardConfig.kpiRange = {
      preset: this.kpiPreset,
      fromDate: isDateRange && this.kpiFromDate ? KpiRangeFns.toIsoDay(this.kpiFromDate) : null,
      toDate: isDateRange && this.kpiToDate ? KpiRangeFns.toIsoDay(this.kpiToDate) : null,
    };
    this.kpiDates = KpiRangeFns.resolve(this.dashboardConfig.kpiRange);
  }

  getWidgetTitle(widgetType: WidgetType): string {
    const info = WIDGET_TYPE_REGISTRY.find((w) => w.type === widgetType);
    return info?.name ?? 'Widget';
  }

  onItemChange(item: GridsterItem): void {
    // Widget position changed - could auto-save or mark dirty
  }

  onItemResize(item: GridsterItem): void {
    // Widget resized - charts may need to re-render
  }

  onAddWidget(): void {
    const dialogRef = this.primengDialogService.open(WidgetPickerDialogComponent, {
      header: 'Add Widget',
      width: '600px',
      modal: true,
    });

    dialogRef.onClose.subscribe((selectedWidget: WidgetTypeInfo | null) => {
      if (selectedWidget) {
        this.addWidgetToGrid(selectedWidget);
      }
    });
  }

  private addWidgetToGrid(widgetInfo: WidgetTypeInfo): void {
    // Generate unique widget ID
    const existingIds = this.dashboardConfig.widgets.map(w => w.id);
    let counter = this.dashboardConfig.widgets.length + 1;
    let newId = `widget-${counter}`;
    while (existingIds.includes(newId)) {
      counter++;
      newId = `widget-${counter}`;
    }

    // Create the new widget config - gridster will find an available position
    const newWidget: DashboardWidgetConfig = {
      id: newId,
      widgetType: widgetInfo.type,
      x: 0,
      y: 0,
      cols: widgetInfo.defaultCols,
      rows: widgetInfo.defaultRows,
    };

    this.dashboardConfig.widgets.push(newWidget);

    this.messageService.add({
      severity: 'success',
      summary: 'Widget Added',
      detail: `${widgetInfo.name} has been added to the dashboard.`,
    });
  }

  onRemoveWidget(widget: DashboardWidgetConfig): void {
    const index = this.dashboardConfig.widgets.indexOf(widget);
    if (index > -1) {
      this.dashboardConfig.widgets.splice(index, 1);
    }
  }

  async onSaveLayout(): Promise<void> {
    try {
      await this.dashboardConfigService.saveConfig(this.dashboardConfig);
      this.messageService.add({
        severity: 'success',
        summary: 'Layout Saved',
        detail: 'Your dashboard layout has been saved.',
      });
    } catch (error) {
      console.error('Error saving dashboard layout:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Save Failed',
        detail: 'Failed to save dashboard layout. Please try again.',
      });
    }
  }

  async onResetLayout(): Promise<void> {
    // The KPI Range is a filter, not part of the layout, so a reset keeps it.
    const kpiRange = this.dashboardConfig.kpiRange;
    this.dashboardConfig = createDefaultDashboardConfig();
    this.dashboardConfig.kpiRange = kpiRange;
    try {
      await this.dashboardConfigService.saveConfig(this.dashboardConfig);
      this.messageService.add({
        severity: 'info',
        summary: 'Layout Reset',
        detail: 'Dashboard has been reset to default layout.',
      });
    } catch (error) {
      console.error('Error saving reset layout:', error);
    }
  }

  async selectAccount() {
    // Use existing account finder dialog pattern
    const { AccountFinderDialogComponent } = await import('@/admin/account-finder-dialog.component');
    const { EntityFinderDialog } = await import('@/shared/entity-finder.dialog');
    const r = await EntityFinderDialog.open(AccountFinderDialogComponent, this);
    if (r.length === 0) return;
    this.account = r[0];
  }

  clearAccount() {
    this.account = null;
  }

  async showExcludedAccounts() {
    const ids = await DialogBaseComponent.open(ExcludedAccountsDialogComponent, this, {
      includedAccountIds: this.includedAccountIds,
      dialogWidth: '500px',
      dialogHeight: '480px',
    });
    if (ids == null) return;
    const isSame = ids.length === this.includedAccountIds.length && ids.every((id) => this.includedAccountIds.includes(id));
    if (!isSame) this.includedAccountIds = ids;
  }

  trackByWidgetId(index: number, widget: DashboardWidgetConfig): string {
    return widget.id;
  }

  /**
   * Check if a widget type supports data viewing/export
   */
  supportsDataView(widgetType: WidgetType): boolean {
    const info = WIDGET_TYPE_REGISTRY.find((w) => w.type === widgetType);
    return info?.supportsDataView ?? false;
  }

  isKpi(widgetType: WidgetType): boolean {
    return WIDGET_TYPE_REGISTRY.find((w) => w.type === widgetType)?.isKpi ?? false;
  }

  usesAccountFilter(widgetType: WidgetType): boolean {
    return WIDGET_TYPE_REGISTRY.find((w) => w.type === widgetType)?.usesAccountFilter ?? false;
  }

  /**
   * Register a widget component instance for later reference
   * Called from widget components via template reference
   */
  registerWidgetComponent(widgetId: string, component: unknown): void {
    this.widgetComponentMap.set(widgetId, component);
  }

  /**
   * Get the widget component for a given widget config
   */
  private getWidgetComponent(widget: DashboardWidgetConfig): ViewableWidget | undefined {
    // Get the appropriate QueryList based on widget type
    let components: unknown[] = [];
    switch (widget.widgetType) {
      case 'jo-in-process':
        components = this.joInProcessWidgets?.toArray() || [];
        break;
      case 'jo-stats':
        components = this.joStatsWidgets?.toArray() || [];
        break;
      case 'invoice-stats':
        components = this.invoiceStatsWidgets?.toArray() || [];
        break;
      case 'invoice-delay':
        components = this.invoiceDelayWidgets?.toArray() || [];
        break;
      case 'inventory-count':
        components = this.manufInventoryWidgets?.toArray() || [];
        break;
      case 'product-type-inventory':
        components = this.productTypeInventoryWidgets?.toArray() || [];
        break;
      case 'account-fulfillment-time':
        components = this.accountFulfillmentTimeWidgets?.toArray() || [];
        break;
      case 'manufacturer-receipt-time':
        components = this.manufacturerReceiptTimeWidgets?.toArray() || [];
        break;
      case 'account-overdue-orders':
        components = this.accountOverdueOrdersWidgets?.toArray() || [];
        break;
      case 'weekly-shipment-revenue':
        components = this.weeklyShipmentRevenueWidgets?.toArray() || [];
        break;
      case 'red-zone':
        components = this.redZoneWidgets?.toArray() || [];
        break;
    }

    // If only one component of this type, return it
    if (components.length === 1) {
      return components[0] as ViewableWidget;
    }

    // For multiple widgets of same type, use the map
    return this.widgetComponentMap.get(widget.id) as ViewableWidget | undefined;
  }

  /**
   * Open the data dialog to view widget data in a grid
   */
  onViewWidgetData(widget: DashboardWidgetConfig): void {
    const component = this.getWidgetComponent(widget);
    if (!component) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Not Available',
        detail: 'Widget data is not available yet.',
      });
      return;
    }

    const params: WidgetDataDialogParams = {
      title: component.getDialogTitle(),
      data: component.getGridData(),
      columnDefs: component.getColumnDefs() as any,
      exportFileName: component.getExportFileName(),
    };

    this.primengDialogService.open(WidgetDataDialogComponent, {
      header: params.title,
      width: '80vw',
      height: '70vh',
      modal: true,
      data: params,
    });
  }

  /**
   * Export widget data directly to Excel
   */
  async onExportWidget(widget: DashboardWidgetConfig): Promise<void> {
    const component = this.getWidgetComponent(widget);
    if (!component) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Not Available',
        detail: 'Widget data is not available for export.',
      });
      return;
    }

    try {
      await component.exportToExcel();
      this.messageService.add({
        severity: 'success',
        summary: 'Export Complete',
        detail: 'Data has been exported to Excel.',
      });
    } catch (error) {
      console.error('Error exporting widget data:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Export Failed',
        detail: 'Failed to export data. Please try again.',
      });
    }
  }
}
